import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
    ArrowLeft, Flame, Target, Zap, TrendingUp, Info, AlertTriangle, 
    ChevronRight, Activity, RefreshCw, FileText, Settings, Clock, Trash2, Plus, X, Database
} from 'lucide-react';
import apiService from '../../services/apiService';
import { isMarketClosed } from '../../utils/marketCalendar';

const Container = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  color: #e5e5e5;
  padding: 20px;
  font-family: 'Inter', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #222;

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 800;
  color: #fff;
  margin: 0;
  letter-spacing: -0.5px;
  
  span {
    color: #e50914;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.2s;
  &:hover { color: #fff; }
`;

const MoodBadge = styled.div`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: ${props => props.$mood === 'HOT' ? 'rgba(229, 9, 20, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$mood === 'HOT' ? '#e50914' : '#888'};
  border: 1px solid ${props => props.$mood === 'HOT' ? '#e50914' : '#333'};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  
  @media (min-width: 1200px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const Card = styled.div`
  background: #141414;
  border: 1px solid #222;
  border-radius: 16px;
  padding: 24px;
  position: relative;
  overflow: hidden;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
`;

const LeaderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th {
    text-align: left;
    color: #666;
    padding: 12px 8px;
    border-bottom: 1px solid #222;
    font-weight: 500;
  }
  
  td {
    padding: 16px 8px;
    border-bottom: 1px solid #1a1a1a;
  }
`;

const GradeBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 800;
  font-size: 12px;
  background: ${props => {
    if (props.$grade === 'S') return '#e50914';
    if (props.$grade === 'A') return '#f59e0b';
    if (props.$grade === 'B') return '#3b82f6';
    return '#333';
  }};
  color: #fff;
`;

const PivotCard = styled.div`
  background: #1a1a1a;
  border: 1px solid ${props => props.$status === 'BREAKOUT' ? 'rgba(229, 9, 20, 0.5)' : '#333'};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    border-color: #e50914;
  }
`;

const StatusTag = styled.span`
  font-size: 10px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.$status === 'BREAKOUT' ? '#e50914' : props.$status === 'NEAR_BREAKOUT' ? '#f59e0b' : '#333'};
  color: #fff;
`;

const TipBox = styled.div`
  background: linear-gradient(135deg, #1e1e1e, #141414);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  margin-top: 24px;
`;

const TipTitle = styled.h4`
  font-size: 14px;
  color: #f59e0b;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TipContent = styled.p`
  font-size: 13px;
  color: #aaa;
  margin: 0;
  line-height: 1.6;
  font-style: italic;
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #666;
`;

const RefreshButton = styled.button`
  background: none;
  border: 1px solid #333;
  color: #888;
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: #fff;
    border-color: #e50914;
    background: rgba(229, 9, 20, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const Modal = styled.div`
  background: #141414;
  border: 1px solid #222;
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  padding: 24px;
`;

function LivermorePage() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [schedulerConfig, setSchedulerConfig] = useState({ 
        enabled: true, 
        times: ['09:30', '13:00', '15:00'], 
        weekdaysOnly: true 
    });
    const [showSettings, setShowSettings] = useState(false);
    const [newTime, setNewTime] = useState('09:00');

    const timerRef = React.useRef(null);
    const isExecutingRef = React.useRef(false); // Execution Lock (Network)
    const lastCheckTimeRef = React.useRef(0);

    const fetchData = async (isManual = false) => {
        if (isManual) setIsSyncing(true);
        else setLoading(true);

        try {
            const res = await apiService.getLivermoreLeaders(isManual);
            setData(res);
        } catch (err) {
            console.error('Failed to fetch Livermore data', err);
        } finally {
            setLoading(false);
            setIsSyncing(false);
        }
    };

    const runAutoSaveProcedure = async (config) => {
        if (!config || !config.enabled || !config.times || config.times.length === 0) return;
        
        const now = new Date();
        const hour = now.getHours();
        const minute = now.getMinutes();
        
        // KST 기준 날짜 문자열 고정 (UTC 기준이면 오전 9시에 날짜가 바뀌는 버그 방지)
        const kstNow = new Date(now.getTime() + 9 * 60 * 60 * 1000);
        const todayStr = kstNow.toISOString().split('T')[0];

        // 1. Silent Holiday/Weekend Check (No Execution)
        if (isMarketClosed() && config.weekdaysOnly) {
            console.log('[Scheduler] Market is closed and weekdaysOnly is true. (Testing: Bypass active)');
            // [TESTING] Temporarily disabled holiday/weekend block
            // return;
        }

        const currentMins = hour * 60 + minute;
        const storageKey = 'livermore_processed_slots';

        try {
            for (const timeStr of config.times) {
                const [sh, sm] = timeStr.split(':').map(Number);
                const slotMins = sh * 60 + sm;

                // 2. Catch-up Logic: Trigger if NOW is past Slot Time
                if (currentMins >= slotMins) {
                    
                    // 3. Persistent Check (localStorage)
                    let processedData = { date: '', slots: {} };
                    try {
                        const stored = localStorage.getItem(storageKey);
                        if (stored) processedData = JSON.parse(stored);
                    } catch (e) {}

                    if (processedData.date === todayStr && processedData.slots[timeStr]) {
                        continue; // Already processed today
                    }

                    // 4. EXECUTION START (Network)
                    if (isExecutingRef.current) {
                        console.log('[Scheduler] Already executing a snapshot pulse. Skipping.');
                        return; 
                    }
                    
                    console.log(`[Scheduler] 🎯 Checking server history for ${timeStr} slot...`);
                    isExecutingRef.current = true;

                    // Final check: server side history
                    const history = await apiService.getLivermoreHistory();
                    const alreadyExists = history && Array.isArray(history) && history.some(h => {
                        if (!h.createdAt) return false;
                        const hDate = new Date(h.createdAt);
                        const hTimeMins = hDate.getHours() * 60 + hDate.getMinutes();
                        const hKstDate = new Date(hDate.getTime() + 9 * 60 * 60 * 1000);
                        const hDateStr = hKstDate.toISOString().split('T')[0];
                        // Window reduced to 5 mins to avoid overlapping slot conflicts
                        return hDateStr === todayStr && Math.abs(hTimeMins - slotMins) < 5;
                    });

                    if (!alreadyExists) {
                        console.log(`[Scheduler] 🚀 Saving snapshot for ${timeStr}...`);
                        await apiService.saveLivermoreSnapshot();
                        await fetchData();
                        console.log(`[Scheduler] ✅ Snapshot saved successfully for ${timeStr}`);
                    } else {
                        console.log(`[Scheduler] ℹ️ Snapshot for ${timeStr} already exists on server. Marking as processed.`);
                    }

                    // Mark as PROCESSED in localStorage
                    processedData.date = todayStr;
                    processedData.slots[timeStr] = true;
                    localStorage.setItem(storageKey, JSON.stringify(processedData));
                    
                    isExecutingRef.current = false;
                }
            }
        } catch (err) {
            console.error('[Scheduler] Critical Error during AutoSave:', err);
            isExecutingRef.current = false;
        }
    };

    // Stabilized Timer Setup
    useEffect(() => {
        fetchData();
        fetchScheduler();
    }, []);

    // High-Precision Scheduler Effect (10s check)
    useEffect(() => {
        if (!schedulerConfig || !schedulerConfig.enabled) return;

        console.log('[Scheduler] Active Monitoring Started (10s Precision)');
        const intervalId = setInterval(() => {
            runAutoSaveProcedure(schedulerConfig);
        }, 10000); 

        return () => {
            console.log('[Scheduler] Cleaning up Monitoring');
            clearInterval(intervalId);
        };
    }, [schedulerConfig?.enabled, schedulerConfig?.times?.length, schedulerConfig?.times?.join(',')]);

    const fetchScheduler = async () => {
        try {
            const res = await apiService.getLivermoreScheduler();
            setSchedulerConfig(res.config);
        } catch (err) {
            console.error('Failed to fetch scheduler config', err);
        }
    };

    const handleUpdateScheduler = async (updates) => {
        try {
            const res = await apiService.updateLivermoreScheduler({ ...schedulerConfig, ...updates });
            if (res.success) {
                setSchedulerConfig(res.status.config);
                // Trigger check immediately after update
                runAutoSaveProcedure(res.status.config);
            } else {
                alert(`Failed to update scheduler: ${res.error || 'Unknown error'}`);
            }
        } catch (err) {
            console.error('Update Error:', err);
            alert(`Failed to update scheduler: ${err.message}`);
        }
    };

    const handleSyncMarketCap = async () => {
        console.log('[SYNC] Button clicked');
        // window.confirm 이 일부 환경에서 차단될 수 있으므로 임시로 직접 실행하거나 alert로 시작을 알립니다.
        alert('시가총액 동기화를 시작합니다. (약 10~20초 소요)');
        
        console.log('[SYNC] Starting synchronization...');
        setIsSyncing(true);
        try {
            const res = await apiService.syncMarketCap();
            console.log('[SYNC] Response received:', res);
            if (res.success) {
                alert(`성공: ${res.count}개 종목의 시가총액이 갱신되었습니다.\n잠시 후 데이터를 다시 불러옵니다.`);
                setTimeout(() => {
                    fetchData(true); // Refresh data
                }, 2500); // 2.5s delay to allow backend stabilization if it auto-restarted
            } else {
                alert(`갱신 실패: ${res.error || 'Unknown error'}`);
            }
        } catch (err) {
            console.error('[SYNC] Sync error:', err);
            alert(`갱신 오류: ${err.message}`);
        } finally {
            setIsSyncing(false);
            console.log('[SYNC] Sync process finished');
        }
    };

    if (loading) {
        return (
            <Container>
                <LoadingOverlay>
                    <Activity size={48} className="animate-pulse" />
                    <p style={{ marginTop: 20 }}>리버모어의 원칙에 따라 시장 주도주를 분석 중입니다...</p>
                </LoadingOverlay>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <TitleSection>
                    <BackButton onClick={() => navigate('/labs')}>
                        <ArrowLeft size={20} />
                    </BackButton>
                    <Title>Jesse Livermore <span>Leaders</span></Title>
                    <MoodBadge $mood={data?.marketMood}>
                        MARKET: {data?.marketMood || 'NEUTRAL'}
                    </MoodBadge>
                    {isMarketClosed() && (
                        <div style={{ color: '#ef4444', fontSize: '10px', fontWeight: 700, background: 'rgba(239, 68, 68, 0.1)', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                            MARKET CLOSED
                        </div>
                    )}
                </TitleSection>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#555', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Last Updated</div>
                        <div style={{ color: '#888', fontSize: '12px', fontWeight: 600 }}>
                            {new Date(data?.timestamp).toLocaleTimeString()}
                            {data?.cached && <span style={{ marginLeft: '6px', color: '#f59e0b', fontSize: '10px' }}>(CACHED)</span>}
                        </div>
                    </div>
                    <RefreshButton onClick={handleSyncMarketCap} disabled={isSyncing} style={{ borderColor: '#e50914', color: '#e50914' }}>
                        <Database size={14} style={{ animation: isSyncing ? 'pulse 1.5s infinite' : 'none' }} />
                        {isSyncing ? 'Syncing...' : 'Update MarketCap'}
                    </RefreshButton>
                    <RefreshButton onClick={() => setShowSettings(true)}>
                        <Settings size={14} />
                        Settings
                        {schedulerConfig?.enabled && (
                            <span 
                                title="Active Monitoring ON"
                                style={{ 
                                    width: 6, height: 6, background: '#22c55e', 
                                    borderRadius: '50%', marginLeft: 4,
                                    boxShadow: '0 0 8px #22c55e'
                                }} 
                            />
                        )}
                    </RefreshButton>
                    <RefreshButton onClick={() => window.open('/labs/livermore/history', '_blank')} style={{ borderColor: '#444', color: '#aaa' }}>
                        <FileText size={14} />
                        History
                    </RefreshButton>
                    <RefreshButton onClick={() => fetchData(true)} disabled={isSyncing}>
                        <RefreshCw size={14} style={{ animation: isSyncing ? 'spin 2s linear infinite' : 'none' }} />
                        {isSyncing ? 'Analyzing...' : 'Refresh'}
                    </RefreshButton>
                </div>
            </Header>

            {showSettings && schedulerConfig && (
                <ModalOverlay onClick={() => setShowSettings(false)}>
                    <Modal onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                            <CardTitle style={{ margin: 0 }}><Clock size={18} /> Scheduler Settings</CardTitle>
                            <BackButton onClick={() => setShowSettings(false)}><X size={20} /></BackButton>
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '14px' }}>
                                <input 
                                    type="checkbox" 
                                    checked={schedulerConfig.enabled} 
                                    onChange={e => handleUpdateScheduler({ enabled: e.target.checked })} 
                                />
                                Enable Auto-Analysis
                            </label>
                            <p style={{ fontSize: '11px', color: '#666', marginTop: 8 }}>
                                When enabled, the app will automatically analyze and save snapshots at the set times when you visit the page.
                            </p>
                        </div>

                        <div style={{ marginBottom: 12, fontSize: '12px', color: '#666', textTransform: 'uppercase' }}>Time Slots</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {schedulerConfig.times.map(time => (
                                <div key={time} style={{ display: 'flex', justifyContent: 'space-between', background: '#0a0a0a', padding: '8px 12px', borderRadius: 8, border: '1px solid #222' }}>
                                    <span>{time}</span>
                                    <button 
                                        onClick={() => handleUpdateScheduler({ times: schedulerConfig.times.filter(t => t !== time) })}
                                        style={{ background: 'none', border: 'none', color: '#e50914', cursor: 'pointer' }}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
                            <input 
                                type="time" 
                                value={newTime} 
                                onChange={e => setNewTime(e.target.value)}
                                style={{ flex: 1, background: '#0a0a0a', border: '1px solid #222', color: '#fff', padding: '8px', borderRadius: 8 }}
                            />
                            <button 
                                onClick={() => {
                                    if (!schedulerConfig.times.includes(newTime)) {
                                        handleUpdateScheduler({ times: [...schedulerConfig.times, newTime].sort() });
                                    }
                                }}
                                style={{ background: '#e50914', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                    </Modal>
                </ModalOverlay>
            )}

            <Grid>
                <Card>
                    <CardTitle><Flame size={20} color="#e50914" /> 주도주 스캐너 (Leading Stocks)</CardTitle>
                    <LeaderTable>
                        <thead>
                            <tr>
                                <th>등급</th>
                                <th>종목명</th>
                                <th>현재가</th>
                                <th>등락률</th>
                                <th>거래대금</th>
                                <th>스코어</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.leaders.map(item => (
                                <tr 
                                    key={item.code} 
                                    onClick={() => window.open(`/labs/stock/technical?symbol=${item.code}${item.resistanceLine ? `&pivot=${item.resistanceLine}` : ''}`, '_blank')} 
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td><GradeBadge $grade={item.grade}>{item.grade}</GradeBadge></td>
                                    <td style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {item.name}
                                        {item.isTrendStarter && (
                                            <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold' }}>
                                                👑초입
                                            </span>
                                        )}
                                    </td>
                                    <td>{item.price?.toLocaleString()}원</td>
                                    <td style={{ color: item.changeRate > 0 ? '#ef4444' : '#3b82f6' }}>
                                        {item.changeRate > 0 ? '+' : ''}{item.changeRate}%
                                    </td>
                                    <td>{(item.trValue / 100000000).toFixed(0)}억</td>
                                    <td style={{ fontWeight: 600, color: '#f59e0b' }}>{item.totalScore.toFixed(0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </LeaderTable>
                </Card>

                <div>
                    <Card>
                        <CardTitle><Target size={20} color="#34d399" /> 전환점 탐지 (Pivot Points)</CardTitle>
                        {data?.pivotPoints.length === 0 ? (
                            <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>
                                현재 저항선을 돌파하거나 횡보 중인 주도주가 없습니다.
                            </p>
                        ) : (
                            data?.pivotPoints.map(item => (
                                <PivotCard 
                                    key={item.code} 
                                    $status={item.pivotStatus}
                                    onClick={() => window.open(`/labs/stock/technical?symbol=${item.code}${item.resistanceLine ? `&pivot=${item.resistanceLine}` : ''}`, '_blank')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <span style={{ fontWeight: 700 }}>{item.name}</span>
                                            <span style={{ fontSize: '13px', color: '#a1a1aa' }}>{item.price?.toLocaleString()}원</span>
                                            <StatusTag $status={item.pivotStatus}>{item.pivotStatus}</StatusTag>
                                            {item.isTrendStarter && (
                                                <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>
                                                    👑장기추세 초입
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                                            거래량 폭발: {item.volSurge?.toFixed(1)}배 | 변동성: {item.volatility} | 대금: {item.trValueFormatted} | 시총: {item.mcapFormatted}
                                            {item.targetUpside && <span style={{marginLeft: 8, color: '#10b981', fontWeight: 600}}>🎯상승여력: {item.targetUpside}</span>}
                                        </div>
                                    </div>
                                    <ChevronRight size={20} color="#444" />
                                </PivotCard>
                            ))
                        )}
                    </Card>

                    <TipBox>
                        <TipTitle><Zap size={16} /> 리버모어의 가르침</TipTitle>
                        <TipContent>
                            "주식 시장에서 일어나는 일은 예전에도 일어났고 앞으로도 계속 일어날 것이다. 
                            이는 인간의 본성이 변하지 않기 때문이다. 주도주가 꺾이기 전까지는 시장을 떠나지 마라."
                        </TipContent>
                    </TipBox>

                    <Card style={{ marginTop: 24, padding: '16px', background: 'rgba(229, 9, 20, 0.05)', borderColor: 'rgba(229, 9, 20, 0.2)' }}>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <AlertTriangle size={20} color="#e50914" />
                            <div>
                                <h5 style={{ margin: '0 0 4px', fontSize: '14px' }}>Livermore Rule #1</h5>
                                <p style={{ margin: 0, fontSize: '12px', color: '#aaa' }}>
                                    미리 사서 기다리지 마세요. 저항선을 뚫고 올라가는 '확인'이 필요합니다.
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>
            </Grid>
        </Container>
    );
}

export default LivermorePage;
